import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBookOpen,
  FiTarget,
  FiCheck,
  FiX,
  FiArrowRight,
  FiZap,
  FiLayers,
  FiRefreshCw,
  FiAward,
  FiTrendingUp,
  FiStar,
  FiLock,
} from 'react-icons/fi';
import TipsStrategiesPanel from './TipsStrategiesPanel';
import { sanitizeHtml } from '../../../utils/safeHtml';
import {
  getCachedComprehensiveProgress,
  getSentenceEquivalenceProgressFromComprehensive,
  getVocabularyProgressFromComprehensive,
  clearVerbalProgressCache,
  GRE_VERBAL_TIPS_ANNOUNCEMENT,
  hasReadGreVerbalTips,
  markGreVerbalTipsAsRead,
} from './api/progressAPI';
import {
  API_BASE_URL,
  getAuthHeaders,
  logout,
  redirectToLoginWithReturn,
} from '../../../utils/utils';
import {
  AUTHENTICATED_USER_MISMATCH_DETAIL,
  buildAuthenticatedUserMismatchMessage,
  createAuthenticatedUserMismatchError,
  isAuthenticatedUserMismatchError,
} from '../../../utils/authUserMismatch';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { renderGreExplanationEmphasis } from '../../../utils/renderGreExplanation';
import GetStartedWith from '../../../components/GetStartedWith';

const buildAuthAwareRequestError = async (response, fallbackMessage) => {
  const errorData = await response.json().catch(() => ({}));

  if (response.status === 401) {
    return new Error('Authentication failed');
  }

  if (
    response.status === 403 &&
    errorData.detail === AUTHENTICATED_USER_MISMATCH_DETAIL
  ) {
    return createAuthenticatedUserMismatchError({
      resourceLabel: 'sentence equivalence exercises',
    });
  }

  return new Error(errorData.detail || fallbackMessage);
};

const localDateKey = () => {
  const value = new Date();
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// API functions
const getSentenceEquivalenceExercises = async (
  difficulty = null,
  topic = null,
  limit = 30
) => {
  try {
    const params = new URLSearchParams();
    if (difficulty) params.append('difficulty', difficulty);
    if (topic) params.append('topic', topic);
    params.append('limit', limit.toString());
    params.append('date', localDateKey());

    const response = await fetch(
      `${API_BASE_URL}/api/gre/exercises/sentence-equivalence?${params}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareRequestError(
        response,
        'Failed to fetch sentence equivalence exercises'
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching sentence equivalence exercises:', error);
    throw error;
  }
};

const recordSentenceEquivalenceAttempt = async (
  exerciseId,
  userAnswer,
  isCorrect,
  timeTaken,
  userEmail
) => {
  try {
    const params = new URLSearchParams();
    params.append('exercise_id', exerciseId.toString());
    params.append('exercise_type', 'sentence_equivalence');
    params.append('user_answer', JSON.stringify(userAnswer)); // Serialize array to JSON string
    params.append('is_correct', isCorrect.toString());
    params.append('time_taken', timeTaken.toString());

    const response = await fetch(
      `${API_BASE_URL}/api/gre/exercises/attempt?${params}`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareRequestError(
        response,
        'Failed to record sentence equivalence attempt'
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error recording sentence equivalence attempt:', error);
    throw error;
  }
};

// API function to save session completion
const saveSessionCompletion = async (sessionData) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/gre/exercises/session-completion`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          exercise_type: 'sentence_equivalence',
          total_questions: sessionData.totalQuestions,
          correct_answers: sessionData.correctAnswers,
          total_time_seconds: sessionData.totalTimeSeconds,
          session_score: sessionData.sessionScore,
          questions_attempted: sessionData.questionsAttempted,
          questions_available: sessionData.questionsAvailable,
          completed_at: new Date().toISOString(),
        }),
      }
    );

    if (!response.ok) {
      throw await buildAuthAwareRequestError(
        response,
        'Failed to save session completion'
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving session completion:', error);
    throw error;
  }
};

const createEmptySentenceEquivalenceProgress = () => ({
  totalQuestions: 0,
  completedQuestions: 0,
  masteredQuestions: 0,
  accuracy: 0,
  overallScore: { correct: 0, total: 0 },
  lastUpdated: null,
  averageTimePerQuestion: 0,
  questionsByDifficulty: {},
  wordPairsMastered: 0,
  targetWordPairs: 100,
  targetAccuracy: 80.0,
  improvementAreas: [],
  recentSessions: [],
  readinessScore: 0,
});

const getDraftStorageKey = (userEmail, suffix) => {
  const normalizedEmail = encodeURIComponent(
    String(userEmail || 'anonymous').toLowerCase()
  );
  return `sentence_equivalence_${suffix}_${normalizedEmail}`;
};

const getDraftStorageKeys = (userEmail) => ({
  progress: getDraftStorageKey(userEmail, 'draft'),
  session: getDraftStorageKey(userEmail, 'session_draft'),
});

const buildScoreFromQuestionResults = (questionResults = {}) => {
  const results = Object.values(questionResults);

  return {
    correct: results.filter((result) => result?.isCorrect).length,
    total: results.length,
  };
};

const filterQuestionResultsForQuestions = (
  questionResults = {},
  practiceQuestions = []
) => {
  const validIds = new Set(
    practiceQuestions.map((question) => String(question.id))
  );

  return Object.fromEntries(
    Object.entries(questionResults).filter(([questionId]) => validIds.has(questionId))
  );
};

// Vocabulary requirements for unlocking exercises (same as Text Completion for consistency)
const VOCABULARY_MILESTONES = [
  {
    words: 0,
    exercises: 0,
    title: 'Start Your Journey',
    description:
      'Ignite your vocabulary mastery adventure and unlock your first exercises! <br/> <b>1000+ sentence equivalence exercises</b> await you!',
  },
  {
    words: 10,
    exercises: 10,
    title: 'Vocabulary Explorer',
    description:
      "Great start! You've unlocked basic exercises. <br/> <b>1000+ additional sentence equivalence exercises</b> await you!",
  },
  {
    words: 25,
    exercises: 15,
    title: 'Word Apprentice',
    description: 'Excellent progress! More challenging exercises await.',
  },
  {
    words: 50,
    exercises: 20,
    title: 'Vocabulary Scholar',
    description: "Impressive! You're accessing advanced exercises.",
  },
  {
    words: 75,
    exercises: 25,
    title: 'Word Master',
    description: 'Outstanding! You have access to expert-level content.',
  },
  {
    words: 100,
    exercises: 30,
    title: 'Vocabulary Champion',
    description: 'Legendary! Most exercises are now unlocked.',
  },
  {
    words: 150,
    exercises: 35,
    title: 'Word Virtuoso',
    description: "Exceptional! You're mastering advanced vocabulary concepts.",
  },
  {
    words: 200,
    exercises: 40,
    title: 'Lexicon Expert',
    description: 'Remarkable! Your vocabulary knowledge is expanding rapidly.',
  },
  {
    words: 300,
    exercises: 45,
    title: 'Verbal Ace',
    description:
      "Outstanding achievement! You're becoming a vocabulary powerhouse.",
  },
  {
    words: 400,
    exercises: 50,
    title: 'Word Savant',
    description: 'Incredible! Your mastery of words is truly impressive.',
  },
  {
    words: 500,
    exercises: 55,
    title: 'Vocabulary Titan',
    description: "Phenomenal! You've reached an elite level of word mastery.",
  },
  {
    words: 650,
    exercises: 60,
    title: 'Lexical Genius',
    description:
      'Extraordinary! Your vocabulary skills are approaching perfection.',
  },
  {
    words: 800,
    exercises: 65,
    title: 'Word Wizard',
    description:
      'Magical! You wield words with incredible precision and power.',
  },
  {
    words: 1000,
    exercises: 70,
    title: 'Vocabulary Legend',
    description:
      "Legendary status achieved! You're among the vocabulary elite.",
  },
  {
    words: 1200,
    exercises: 75,
    title: 'Ultimate Word Master',
    description:
      "Supreme achievement! You've mastered the complete vocabulary spectrum.",
  },
];

// Helper function to determine unlocked exercises based on mastered words
const getUnlockStatus = (masteredWords, exercises) => {
  const wordCount = masteredWords || 0;

  // Find current milestone
  const currentMilestone =
    VOCABULARY_MILESTONES.slice()
      .reverse()
      .find((milestone) => wordCount >= milestone.words) ||
    VOCABULARY_MILESTONES[0];

  // Find next milestone
  const nextMilestone = VOCABULARY_MILESTONES.find(
    (milestone) => milestone.words > wordCount
  );

  const unlockedExercises = Math.min(
    currentMilestone.exercises,
    exercises.length
  );

  return {
    currentMilestone,
    nextMilestone,
    unlockedExercises,
    totalExercises: exercises.length,
    progress: wordCount / (nextMilestone?.words || 1200),
  };
};

// Sentence Equivalence tips and strategies
const sentenceEquivalenceTips = [
  {
    category: 'Core Strategies',
    icon: <FiTarget />,
    tips: [
      {
        title: 'Understand the Sentence Before the Choices',
        content:
          'Your first job is to understand what the sentence is trying to say. Ask what the writer is doing: supporting, contrasting, qualifying, or challenging an idea.',
        importance: 'high',
      },
      {
        title: 'Predict the Missing Idea in Simple Words',
        content:
          'Before you look at the options, describe the blank in plain language such as "negative," "surprising," "not important," or "careful." A rough prediction is enough.',
        importance: 'high',
      },
      {
        title: 'Paraphrase the Sentence in Your Own Words',
        content:
          'Translate the sentence into simpler language in your head. This prevents complex wording from hiding the real logic of the blank.',
        importance: 'high',
      },
      {
        title: 'Match Meaning, Not Dictionary Similarity',
        content:
          'The correct pair must make the whole sentence mean essentially the same thing. Two words can be close in definition but still fail if they create different meanings in context.',
        importance: 'high',
      },
    ],
  },
  {
    category: 'Question Analysis',
    icon: <FiLayers />,
    tips: [
      {
        title: 'Watch Contrast and Support Words',
        content:
          'Words like "but," "however," "although," and "yet" usually reveal the real turn in meaning. Support words such as "moreover" and "indeed" tell you the blank should continue the same idea.',
        importance: 'high',
      },
      {
        title: 'Track the Sentence Logic',
        content:
          'Read the sentence as a structure: claim then contrast, expectation then surprise, or description then conclusion. The blank must fit that logical role.',
        importance: 'high',
      },
      {
        title: 'Check Tone and Register',
        content:
          'Notice whether the sentence sounds critical, supportive, neutral, restrained, or extreme. Both correct choices must match that tone, not just the topic.',
        importance: 'high',
      },
      {
        title: 'Use the Rest of the Sentence as Evidence',
        content:
          'Small clues after the blank often tell you what must be true. If the sentence mentions caution, surprise, praise, or limitation, let those clues control your choice.',
        importance: 'medium',
      },
    ],
  },
  {
    category: 'Elimination and Pairing',
    icon: <FiZap />,
    tips: [
      {
        title: 'Use Elimination, Not Perfection',
        content:
          'You do not need to know the exact word immediately. Eliminate answers with the wrong tone, wrong logic, or wrong level of intensity, then compare the strongest survivors.',
        importance: 'high',
      },
      {
        title: 'Throw Out Real-Life but Wrong-in-Context Words',
        content:
          'Some options sound reasonable in everyday English but do not fit the sentence\'s argument. The passage logic matters more than whether a word feels generally appropriate.',
        importance: 'medium',
      },
      {
        title: 'Test Both Choices in the Full Sentence',
        content:
          'After narrowing down options, plug each candidate back into the sentence. Both correct answers must create logical sentences with essentially the same overall meaning.',
        importance: 'medium',
      },
      {
        title: 'Check Degree and Emotional Force',
        content:
          'Two words can point in the same direction but still fail if one is too strong, too mild, or emotionally off. Equivalent answers should match not only direction, but also intensity.',
        importance: 'medium',
      },
    ],
  },
  {
    category: 'Reading Sentence Nuance',
    icon: <FiBookOpen />,
    isNew: true,
    tips: [
      {
        title: 'Find the Author\'s Actual Position',
        content:
          'If the sentence presents one view and then pivots, the author\'s real point usually comes after the pivot. Read for intention, not just surface wording.',
        example:
          '"Some critics dismissed the policy as ineffective, but later evidence suggested the reform was surprisingly ______." — The word "but" signals the real point. The blank should mean effective or successful, not flawed.',
        importance: 'high',
        isNew: true,
      },
      {
        title: 'Paraphrase Dense Wording Immediately',
        content:
          'When a sentence feels abstract or heavy, restate it simply before touching the options. A clear paraphrase often reveals the missing idea faster than vocabulary lookup.',
        example:
          '"Recent findings, once regarded as merely speculative, now appear far less ______ than early critics had assumed." — Simple paraphrase: "People thought the findings were doubtful, but now they seem more solid." The blank should mean doubtful or uncertain.',
        importance: 'high',
        isNew: true,
      },
      {
        title: 'Handle Negation and Qualification Carefully',
        content:
          'Phrases like "not," "hardly," "by no means," and "not entirely" often reverse or soften the meaning. Slow down and translate the sentence into a direct statement.',
        example:
          '"Her achievements were by no means ______, having reshaped the entire field." — Because the phrase negates the blank, the missing word should mean minor or trivial, not important.',
        importance: 'high',
        isNew: true,
      },
      {
        title: 'Read Concession as a Clue About Strength',
        content:
          'Words like "although," "even though," and "while" often signal that the sentence is admitting a limited weakness before making a larger point. That usually rules out extreme answers.',
        example:
          '"Although the article was occasionally ______, its central argument remained persuasive." — "Occasionally" and "although" suggest a mild criticism such as dull or repetitive, not a total failure like incoherent.',
        importance: 'high',
        isNew: true,
      },
    ],
  },
].map((category) => ({
  ...category,
  isNew: true,
  tips: category.tips.map((tip) => ({
    ...tip,
    isNew: true,
  })),
}));

const sentenceEquivalenceQuickHits = [
  {
    label: 'Start Here',
    title: 'Understand the sentence, then predict',
    content:
      'Ignore the choices at first and describe the blank in your own rough words before you start comparing options, even if that rough word comes to mind in your native language.',
  },
  {
    label: 'Best Move',
    title: 'Eliminate by tone and logic',
    content:
      'Remove answers that clash with the sentence\'s tone, contrast, or argument, even if the word seems reasonable by itself. Also eliminate answers that are not close to the rough word you predicted.',
  },
  {
    label: 'Common Trap',
    title: 'Do not chase perfect synonyms',
    content:
      'The right pair creates the same meaning in context. Similar-looking words are wrong if they change the sentence\'s force or direction, so keep only the two words that stay closest to your rough prediction at the end.',
  },
];

// Helper function for other components to access canonical Sentence Equivalence progress
export const getSentenceEquivalenceProgressData = async (userEmail) => {
  try {
    if (!userEmail) {
      return createEmptySentenceEquivalenceProgress();
    }

    // First try to get data from comprehensive backend API
    const comprehensiveProgress =
      await getCachedComprehensiveProgress(userEmail);
    const backendData = getSentenceEquivalenceProgressFromComprehensive(
      comprehensiveProgress
    );

    return {
      totalQuestions: backendData.totalQuestions,
      completedQuestions: backendData.totalQuestions,
      masteredQuestions: backendData.correctAnswers,
      accuracy: backendData.accuracy,
      overallScore: {
        correct: backendData.correctAnswers,
        total: backendData.totalQuestions,
      },
      lastUpdated: backendData.lastUpdated,
      questionResults: {}, // Canonical progress comes from backend aggregates
      averageTimePerQuestion: backendData.averageTimePerQuestion,
      questionsByDifficulty: backendData.questionsByDifficulty,
      wordPairsMastered: backendData.wordPairsMastered,
      targetWordPairs: backendData.targetWordPairs,
      targetAccuracy: backendData.targetAccuracy,
      improvementAreas: backendData.improvementAreas,
      recentSessions: backendData.recentSessions,
      readinessScore: backendData.readinessScore,
    };
  } catch (error) {
    console.error('Error accessing Sentence Equivalence progress:', error);
    return createEmptySentenceEquivalenceProgress();
  }
};

export default function SentenceEquivalence() {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [user] = useState(() => {
    try {
      const storedUser = sessionStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Error parsing user from sessionStorage:', error);
      return null;
    }
  });
  const [userEmail, setUserEmail] = useState(null);

  const [showTips, setShowTips] = useState(true);
  const [hasReadTips, setHasReadTips] = useState(false);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authMismatch, setAuthMismatch] = useState(null);
  const [exerciseStartTime, setExerciseStartTime] = useState(null);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);

  // Vocabulary progress tracking
  const [vocabularyProgress, setVocabularyProgress] = useState(null);
  const [unlockStatus, setUnlockStatus] = useState(null);

  // Progress tracking state
  const [questionResults, setQuestionResults] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [overallScore, setOverallScore] = useState({ correct: 0, total: 0 });
  const [sessionProgress, setSessionProgress] = useState([]);

  const redirectToLogin = (
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (authRedirectStartedRef.current) {
      return;
    }

    authRedirectStartedRef.current = true;
    redirectToLoginWithReturn({
      navigate,
      redirectTo: window.location.pathname + window.location.search,
      message,
    });
  };

  // Practice mode state
  const [practiceMode, setPracticeMode] = useState('unmastered'); // 'unmastered', 'attempted', 'mastered', 'all'
  const [availablePracticeModes, setAvailablePracticeModes] = useState({
    unmastered: 0,
    attempted: 0,
    mastered: 0,
  });

  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
    } else {
      // Try localStorage as final fallback
      const storedEmail = localStorage.getItem('userEmail');
      if (storedEmail) {
        setUserEmail(storedEmail);
      }
    }
  }, [user]);

  useEffect(() => {
    const alreadyRead = hasReadGreVerbalTips('sentence_equivalence');
    setHasReadTips(alreadyRead);

    if (!alreadyRead) {
      setShowTips(true);
    }
  }, [userEmail]);

  // Refs to track ongoing API requests
  const ongoingRequests = useRef({});

  const saveProgressToStorage = useCallback(
    (progressData, email = userEmail) => {
      try {
        if (!email) return;
        const { progress } = getDraftStorageKeys(email);
        localStorage.setItem(progress, JSON.stringify(progressData));
      } catch (error) {
        console.error('Error saving progress to localStorage:', error);
      }
    },
    [userEmail]
  );

  const loadProgressFromStorage = useCallback(
    (email = userEmail) => {
      try {
        if (!email) return null;
        const { progress } = getDraftStorageKeys(email);
        const saved = localStorage.getItem(progress);
        return saved ? JSON.parse(saved) : null;
      } catch (error) {
        console.error('Error loading progress from localStorage:', error);
        return null;
      }
    },
    [userEmail]
  );

  const saveSessionToStorage = useCallback(
    (sessionData, email = userEmail) => {
      try {
        if (!email) return;
        const { session } = getDraftStorageKeys(email);
        localStorage.setItem(session, JSON.stringify(sessionData));
      } catch (error) {
        console.error('Error saving session to localStorage:', error);
      }
    },
    [userEmail]
  );

  const loadSessionFromStorage = useCallback(
    (email = userEmail) => {
      try {
        if (!email) return null;
        const { session } = getDraftStorageKeys(email);
        const saved = localStorage.getItem(session);
        return saved ? JSON.parse(saved) : null;
      } catch (error) {
        console.error('Error loading session from localStorage:', error);
        return null;
      }
    },
    [userEmail]
  );

  const clearStoredDraft = useCallback(
    (email = userEmail) => {
      try {
        if (!email) return;
        const keys = getDraftStorageKeys(email);
        localStorage.removeItem(keys.progress);
        localStorage.removeItem(keys.session);
      } catch (error) {
        console.error('Error clearing stored progress data:', error);
      }
    },
    [userEmail]
  );

  // Clear all progress data
  const clearAllProgress = () => {
    try {
      clearStoredDraft();
      setQuestionResults({});
      setOverallScore({ correct: 0, total: 0 });
      setCurrentQuestionIndex(0);
      setSessionProgress([]);
      setSelectedQuestion(null);
      setSessionSaved(false);
      setSessionStartTime(null);
      setExerciseStartTime(null);
      setUserAnswers([]);
      setShowResults(false);
    } catch (error) {
      console.error('Error clearing progress data:', error);
    }
  };

  // Helper function to shuffle question options while maintaining correct answer references
  const shuffleQuestionOptions = (question) => {
    if (!question || !question.options || !Array.isArray(question.options)) {
      return question;
    }

    // Create a copy of the question to avoid mutating the original
    const shuffledQuestion = { ...question };

    // Create array of options with their original indices
    const optionsWithIndex = question.options.map((option, index) => ({
      option,
      originalIndex: index,
    }));

    // Shuffle the options array using Fisher-Yates algorithm
    for (let i = optionsWithIndex.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [optionsWithIndex[i], optionsWithIndex[j]] = [
        optionsWithIndex[j],
        optionsWithIndex[i],
      ];
    }

    // Extract the shuffled options
    shuffledQuestion.options = optionsWithIndex.map((item) => item.option);

    // The correctAnswers should remain the same since they reference the actual text values
    // not the indices, so no need to update them

    return shuffledQuestion;
  };

  // Helper function to get filtered questions based on practice mode
  const isTodayQuestion = (question) =>
    question.role === 'today' ||
    (question.role == null &&
      (question.status === 'unmastered' || question.status === 'pending'));

  const getFilteredQuestions = () => {
    if (!practiceQuestions.length || !unlockStatus) return [];

    switch (practiceMode) {
      case 'attempted':
        return practiceQuestions.filter((q) => q.role === 'review');
      case 'mastered':
        return practiceQuestions.filter((q) => q.role === 'mastered');
      case 'unmastered':
      case 'all':
      default:
        return practiceQuestions.filter((q) => isTodayQuestion(q));
    }
  };

  // Enhanced API functions with duplicate prevention
  const getSentenceEquivalenceExercisesWithRef = async (
    difficulty = null,
    topic = null,
    limit = 30,
    userEmail
  ) => {
    const requestKey = `exercises_${difficulty || 'all'}_${topic || 'all'}_${limit}`;

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Create new request promise
    const requestPromise = getSentenceEquivalenceExercises(
      difficulty,
      topic,
      limit,
      userEmail
    );

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  const getVocabularyMasteryProgressDataWithRef = async (userEmail) => {
    const requestKey = 'vocabulary_progress';

    // Early return if userEmail is not provided
    if (!userEmail) {
      console.warn(
        'getVocabularyMasteryProgressDataWithRef: userEmail is null or empty'
      );
      throw new Error('User email is required for vocabulary progress data');
    }

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Create new request promise - get comprehensive progress and extract vocabulary data
    const requestPromise = getCachedComprehensiveProgress(userEmail).then(
      (comprehensiveData) =>
        getVocabularyProgressFromComprehensive(comprehensiveData)
    );

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  useEffect(() => {
    const loadData = async () => {
      // Don't load data if userEmail is not available yet
      if (!userEmail) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setAuthMismatch(null);

        // Get vocabulary progress first
        const vocabProgress =
          await getVocabularyMasteryProgressDataWithRef(userEmail);
        setVocabularyProgress(vocabProgress);

        // Calculate how many exercises should be unlocked based on current vocabulary progress
        const masteredWords = vocabProgress.masteredWords || 0;
        const currentMilestone =
          VOCABULARY_MILESTONES.slice()
            .reverse()
            .find((milestone) => masteredWords >= milestone.words) ||
          VOCABULARY_MILESTONES[0];

        const exercisesToFetch = currentMilestone.exercises;

        // Only fetch exercises up to the unlocked amount (but at least 1 if any words are mastered)
        const limitToFetch =
          exercisesToFetch > 0 ? exercisesToFetch : masteredWords > 0 ? 5 : 0;

        let transformedQuestions = [];
        let exerciseStats = {
          unmastered: 0,
          attempted: 0,
          mastered: 0,
          total: 0,
        };
        console.log('limitToFetch', limitToFetch);
        // Fetch sentence equivalence exercises from backend
        const exerciseGroups = await getSentenceEquivalenceExercisesWithRef(
          null,
          null,
          limitToFetch,
          userEmail
        );

        // Handle new grouped response format
        const todayGroup = exerciseGroups.today || null;
        const allExercises = [
          ...(todayGroup?.exercises || exerciseGroups.unmastered || []).map(
            (ex) => ({
              ...ex,
              status: ex.status || 'pending',
              role: ex.role || 'today',
            })
          ),
          ...(exerciseGroups.attempted || []).map((ex) => ({
            ...ex,
            status: 'attempted',
            role: ex.role || 'review',
          })),
          ...(exerciseGroups.mastered || []).map((ex) => ({
            ...ex,
            status: 'mastered',
            role: ex.role || 'mastered',
          })),
        ];

        exerciseStats = {
          unmastered: todayGroup?.target || exerciseGroups.total_unmastered || 0,
          pending: todayGroup?.pending ?? exerciseGroups.total_unmastered ?? 0,
          attempted: exerciseGroups.total_attempted || 0,
          mastered: exerciseGroups.total_mastered || 0,
          total: todayGroup?.target || exerciseGroups.total_exercises || 0,
          todayCompleted: Boolean(todayGroup?.completed),
          todayDate: todayGroup?.date || null,
        };

        // Transform backend data to match component structure
        transformedQuestions = allExercises.map((exercise, index) => ({
          id: exercise.id || `se-${index}`,
          sentence: exercise.sentence || exercise.passage,
          options: exercise.options || [],
          correctAnswers: exercise.correct_pair,
          explanation: exercise.explanation,
          difficulty: exercise.difficulty_level || 'medium',
          status: exercise.status,
          role: exercise.role || 'today',
          carriedOver: Boolean(exercise.carried_over),
          topic: exercise.topic || 'General',
          sourceWord: exercise.target_word,
          requiredWords: exercise.required_mastered_words || [],
        }));

        setPracticeQuestions(transformedQuestions);

        // Update available practice modes
        setAvailablePracticeModes({
          unmastered: exerciseStats.unmastered,
          pending: exerciseStats.pending,
          attempted: exerciseStats.attempted,
          mastered: exerciseStats.mastered,
          todayCompleted: exerciseStats.todayCompleted,
        });

        // Automatically select the best practice mode
        if (exerciseStats.unmastered > 0) {
          setPracticeMode('unmastered');
        } else if (exerciseStats.attempted > 0) {
          setPracticeMode('attempted');
        } else if (exerciseStats.mastered > 0) {
          setPracticeMode('mastered');
        } else {
          setPracticeMode('unmastered'); // Default fallback
        }

        // Calculate unlock status
        const unlock = getUnlockStatus(
          vocabProgress.masteredWords,
          transformedQuestions
        );
        setUnlockStatus({ ...unlock, exerciseStats });

        // Load saved progress
        const savedProgress = loadProgressFromStorage(userEmail);
        const savedSession = loadSessionFromStorage(userEmail);
        if (savedProgress) {
          const filteredQuestionResults = filterQuestionResultsForQuestions(
            savedProgress.questionResults || {},
            transformedQuestions
          );
          const restoredScore =
            savedProgress.overallScore &&
            savedProgress.overallScore.total ===
              Object.keys(filteredQuestionResults).length
              ? savedProgress.overallScore
              : buildScoreFromQuestionResults(filteredQuestionResults);
          const restoredQuestion =
            transformedQuestions.find(
              (question) =>
                String(question.id) === String(savedProgress.selectedQuestionId)
            ) || null;

          setQuestionResults(filteredQuestionResults);
          setOverallScore(restoredScore);
          setCurrentQuestionIndex(savedProgress.currentQuestionIndex || 0);
          setSessionProgress(savedProgress.sessionProgress || []);
          setSessionSaved(savedProgress.sessionSaved || false);
          setSessionStartTime(savedProgress.sessionStartTime || null);
          setPracticeMode(savedProgress.practiceMode || 'unmastered');

          if (restoredQuestion) {
            setSelectedQuestion(shuffleQuestionOptions(restoredQuestion));
            setExerciseStartTime(Date.now());
          }
        }

        if (savedSession) {
          setSessionStartTime(savedSession.sessionStartTime || null);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        if (error?.message === 'Authentication failed') {
          redirectToLogin();
          return;
        }
        if (isAuthenticatedUserMismatchError(error)) {
          const mismatchState = {
            currentEmail: user?.email || null,
            requestedEmail: error.requestedEmail || null,
            resourceLabel:
              error.resourceLabel || 'sentence equivalence exercises',
          };
          const mismatchMessage =
            buildAuthenticatedUserMismatchMessage(mismatchState);

          setAuthMismatch({ ...mismatchState, message: mismatchMessage });
          setError(mismatchMessage);

          const shouldSwitch = window.confirm(
            `${mismatchMessage}\n\nDo you want to log out and sign in with the correct account to continue?`
          );

          if (shouldSwitch) {
            await logout();
            return;
          }
        } else {
          setError(
            (error && error.message) ||
              'Something went wrong while loading exercises. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userEmail, loadProgressFromStorage, loadSessionFromStorage]); // Add userEmail as dependency so it runs when userEmail is set

  // Save session completion to backend when session ends
  useEffect(() => {
    const saveSessionToBackend = async () => {
      // Only save if session has activities and hasn't been saved yet
      if (
        sessionProgress.length > 0 &&
        !selectedQuestion &&
        !sessionSaved &&
        overallScore.total > 0
      ) {
        try {
          const sessionDurationSeconds = sessionProgress.reduce(
            (total, result) => total + (result.timeTaken || 0),
            0
          );

          const sessionData = {
            totalQuestions: overallScore.total,
            correctAnswers: overallScore.correct,
            totalTimeSeconds: sessionDurationSeconds,
            sessionScore: Math.round(
              (overallScore.correct / overallScore.total) * 100
            ),
            questionsAttempted: sessionProgress.map((result) => ({
              questionId: result.questionId,
              isCorrect: result.isCorrect,
              userAnswers: result.userAnswers,
              timestamp: result.timestamp,
            })),
            questionsAvailable: practiceQuestions.map((question) => ({
              id: question.id,
            })),
            userEmail: userEmail,
          };

          await saveSessionCompletion(sessionData);
          clearVerbalProgressCache(userEmail);
          clearStoredDraft(userEmail);
          setSessionSaved(true);
          console.log('Sentence equivalence session saved to backend');
        } catch (error) {
          console.error('Error saving session completion:', error);
          // Don't prevent user from continuing if save fails
        }
      }
    };

    saveSessionToBackend();
  }, [
    userEmail,
    sessionProgress,
    selectedQuestion,
    sessionSaved,
    overallScore,
    sessionStartTime,
    practiceQuestions,
    clearStoredDraft,
  ]);

  // Save progress whenever it changes
  useEffect(() => {
    if (practiceQuestions.length > 0) {
      const progressData = {
        questionResults,
        overallScore,
        currentQuestionIndex,
        totalQuestions: practiceQuestions.length,
        selectedQuestionId: selectedQuestion?.id || null,
        practiceMode,
        sessionProgress,
        sessionSaved,
        sessionStartTime,
        lastUpdated: new Date().toISOString(),
      };
      saveProgressToStorage(progressData);
    }
  }, [
    questionResults,
    overallScore,
    currentQuestionIndex,
    practiceQuestions.length,
    selectedQuestion,
    practiceMode,
    sessionProgress,
    sessionSaved,
    sessionStartTime,
    userEmail,
    saveProgressToStorage,
  ]);

  useEffect(() => {
    if (!userEmail) return;

    saveSessionToStorage(
      {
        sessionStartTime,
        lastUpdated: new Date().toISOString(),
      },
      userEmail
    );
  }, [sessionStartTime, userEmail, saveSessionToStorage]);

  const handleStartPractice = () => {
    if (!hasReadTips) {
      setShowTips(true);
      return;
    }

    if (!unlockStatus || unlockStatus.unlockedExercises === 0) {
      return; // Can't start if no exercises are unlocked
    }

    const availableQuestions = getFilteredQuestions();
    if (availableQuestions.length === 0) {
      return; // No unlocked questions available in this practice mode
    }

    const hasPending = availableQuestions.some(
      (q) => q.status === 'pending' || q.status === 'unmastered'
    );
    if (
      (practiceMode === 'unmastered' || practiceMode === 'all') &&
      !hasPending
    ) {
      return;
    }

    const nextUncompletedIndex = availableQuestions.findIndex(
      (q) =>
        !questionResults[q.id] &&
        (q.status === 'pending' || q.status === 'unmastered')
    );

    if (nextUncompletedIndex !== -1) {
      setSelectedQuestion(
        shuffleQuestionOptions(availableQuestions[nextUncompletedIndex])
      );
      setCurrentQuestionIndex(nextUncompletedIndex);
      setSessionStartTime(Date.now());
      setExerciseStartTime(Date.now());
    } else {
      // All unlocked questions completed, start from beginning
      setSelectedQuestion(shuffleQuestionOptions(availableQuestions[0]));
      setCurrentQuestionIndex(0);
      setSessionStartTime(Date.now());
      setExerciseStartTime(Date.now());
    }
    setUserAnswers([]);
    setShowResults(false);
    setSessionSaved(false);
  };

  const handleAnswerToggle = (option) => {
    setUserAnswers((prev) => {
      const newAnswers = prev.includes(option)
        ? prev.filter((answer) => answer !== option)
        : [...prev, option];
      return newAnswers;
    });
  };

  const checkAnswerCorrectness = () => {
    if (!selectedQuestion || !selectedQuestion.correctAnswers) return false;

    const correctAnswers = Array.isArray(selectedQuestion.correctAnswers)
      ? selectedQuestion.correctAnswers
      : [selectedQuestion.correctAnswers];

    const correctSet = new Set(correctAnswers);
    const userSet = new Set(userAnswers);

    return (
      correctSet.size === userSet.size &&
      [...correctSet].every((answer) => userSet.has(answer))
    );
  };

  const handleSubmitQuestion = async () => {
    const isCorrect = checkAnswerCorrectness();
    const timeTaken = exerciseStartTime
      ? Math.round((Date.now() - exerciseStartTime) / 1000)
      : 0;

    const questionResult = {
      questionId: selectedQuestion.id,
      isCorrect,
      isCompleted: true,
      mastered: isCorrect,
      timeTaken,
      userAnswers: [...userAnswers],
      timestamp: new Date().toISOString(),
    };

    // Update question results
    setQuestionResults((prev) => ({
      ...prev,
      [selectedQuestion.id]: questionResult,
    }));

    // Update overall score
    setOverallScore((prev) => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1,
    }));

    // Add to session progress
    setSessionProgress((prev) => [...prev, questionResult]);

    try {
      // Record attempt in backend
      await recordSentenceEquivalenceAttempt(
        selectedQuestion.id,
        userAnswers,
        isCorrect,
        timeTaken,
        userEmail
      );
      clearVerbalProgressCache(userEmail);
    } catch (error) {
      console.error('Error recording sentence equivalence attempt:', error);
      // Don't prevent user from continuing if API call fails
    }

    // Show results for this question
    setShowResults(true);
  };

  const handleNextQuestion = () => {
    const nextIndex = currentQuestionIndex + 1;
    const availableQuestions = getFilteredQuestions();

    if (nextIndex < availableQuestions.length) {
      setSelectedQuestion(
        shuffleQuestionOptions(availableQuestions[nextIndex])
      );
      setCurrentQuestionIndex(nextIndex);
      setShowResults(false);
      setUserAnswers([]);
      setExerciseStartTime(Date.now());
    } else {
      // Practice session complete - show summary
      setSelectedQuestion(null);
      setShowResults(false);
      setUserAnswers([]);
    }
  };

  const renderTips = () => (
    <TipsStrategiesPanel
      title="Sentence Equivalence Tips & Strategies"
      subtitle="Based on recent official results from some of our members and learners, these are the new techniques we want you using now and going forward."
      categories={sentenceEquivalenceTips}
      quickHits={sentenceEquivalenceQuickHits}
      showTips={showTips}
      onToggle={() => {
        if (!hasReadTips) {
          setShowTips(true);
          return;
        }
        setShowTips((prev) => !prev);
      }}
      announcement={GRE_VERBAL_TIPS_ANNOUNCEMENT}
      requireAcknowledgement={!hasReadTips}
      hasAcknowledged={hasReadTips}
      onAcknowledge={async () => {
        const success = await markGreVerbalTipsAsRead('sentence_equivalence');
        if (success) {
          setHasReadTips(true);
        }
      }}
      acknowledgeLabel="I have read the new Sentence Equivalence techniques"
    />
  );

  const renderPracticeSection = () => {
    if (!hasReadTips) {
      return (
        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            border: '1px solid rgba(255, 107, 53, 0.25)',
            borderRadius: '16px',
            padding: '1.5rem',
            marginTop: '1rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              color: '#d95d2c',
              fontWeight: 700,
              marginBottom: '0.75rem',
            }}
          >
            <FiLock />
            Practice unlocks after you read the new techniques
          </div>
          <p
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              margin: 0,
              lineHeight: 1.6,
            }}
          >
            Review the updated tips above, then confirm that you have read them to
            unlock Sentence Equivalence practice on this account across devices.
          </p>
          <button
            onClick={async () => {
              const success = await markGreVerbalTipsAsRead(
                'sentence_equivalence'
              );
              if (success) {
                setHasReadTips(true);
              }
            }}
            style={{
              marginTop: '1rem',
              background: '#d95d2c',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            I have read the new Sentence Equivalence techniques
          </button>
        </div>
      );
    }

    if (!vocabularyProgress || !unlockStatus) return null;

    const filteredQuestions = getFilteredQuestions();

    return (
      <div>
        <h2
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1.5rem',
          }}
        >
          Practice Exercises
        </h2>

        {/* Practice Mode Selector */}
        {unlockStatus.exerciseStats && (
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h3
              style={{
                color: 'var(--liwaza-on-surface)',
                marginBottom: '1rem',
                fontSize: '1.1rem',
              }}
            >
              Choose Practice Mode
            </h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
              }}
            >
              <button
                onClick={() => setPracticeMode('unmastered')}
                disabled={availablePracticeModes.unmastered === 0}
                style={{
                  background:
                    practiceMode === 'unmastered'
                      ? 'var(--liwaza-primary)'
                      : availablePracticeModes.unmastered === 0
                        ? 'var(--liwaza-surface-variant)'
                        : 'var(--liwaza-surface-container-highest)',
                  color:
                    practiceMode === 'unmastered'
                      ? 'white'
                      : availablePracticeModes.unmastered === 0
                        ? 'var(--liwaza-on-surface-variant)'
                        : 'var(--liwaza-on-surface)',
                  border:
                    practiceMode === 'unmastered'
                      ? '2px solid var(--liwaza-primary)'
                      : '1px solid var(--liwaza-outline-variant)',
                  padding: '1rem',
                  borderRadius: '8px',
                  cursor:
                    availablePracticeModes.unmastered === 0
                      ? 'not-allowed'
                      : 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiTarget />
                  Today's exercises ({availablePracticeModes.pending ?? 0} left of{' '}
                  {availablePracticeModes.unmastered})
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Fixed for today. Finishing one does not add another.
                </div>
              </button>

              <button
                onClick={() => setPracticeMode('attempted')}
                disabled={availablePracticeModes.attempted === 0}
                style={{
                  background:
                    practiceMode === 'attempted'
                      ? 'var(--liwaza-primary)'
                      : availablePracticeModes.attempted === 0
                        ? 'var(--liwaza-surface-variant)'
                        : 'var(--liwaza-surface-container-highest)',
                  color:
                    practiceMode === 'attempted'
                      ? 'white'
                      : availablePracticeModes.attempted === 0
                        ? 'var(--liwaza-on-surface-variant)'
                        : 'var(--liwaza-on-surface)',
                  border:
                    practiceMode === 'attempted'
                      ? '2px solid var(--liwaza-primary)'
                      : '1px solid var(--liwaza-outline-variant)',
                  padding: '1rem',
                  borderRadius: '8px',
                  cursor:
                    availablePracticeModes.attempted === 0
                      ? 'not-allowed'
                      : 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiRefreshCw />
                  Review missed ({availablePracticeModes.attempted})
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Earlier mistakes. Not part of today or tomorrow.
                </div>
              </button>

              <button
                onClick={() => setPracticeMode('mastered')}
                disabled={availablePracticeModes.mastered === 0}
                style={{
                  background:
                    practiceMode === 'mastered'
                      ? 'var(--liwaza-primary)'
                      : availablePracticeModes.mastered === 0
                        ? 'var(--liwaza-surface-variant)'
                        : 'var(--liwaza-surface-container-highest)',
                  color:
                    practiceMode === 'mastered'
                      ? 'white'
                      : availablePracticeModes.mastered === 0
                        ? 'var(--liwaza-on-surface-variant)'
                        : 'var(--liwaza-on-surface)',
                  border:
                    practiceMode === 'mastered'
                      ? '2px solid var(--liwaza-primary)'
                      : '1px solid var(--liwaza-outline-variant)',
                  padding: '1rem',
                  borderRadius: '8px',
                  cursor:
                    availablePracticeModes.mastered === 0
                      ? 'not-allowed'
                      : 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiAward />
                  Mastered ({availablePracticeModes.mastered})
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  Already solved. Extra practice, not a new daily set.
                </div>
              </button>

              <button
                onClick={() => setPracticeMode('all')}
                disabled={practiceQuestions.length === 0}
                style={{
                  background:
                    practiceMode === 'all'
                      ? 'var(--liwaza-primary)'
                      : 'var(--liwaza-surface-container-highest)',
                  color:
                    practiceMode === 'all'
                      ? 'white'
                      : 'var(--liwaza-on-surface)',
                  border:
                    practiceMode === 'all'
                      ? '2px solid var(--liwaza-primary)'
                      : '1px solid var(--liwaza-outline-variant)',
                  padding: '1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <FiLayers />
                  Today's set ({unlockStatus.exerciseStats?.total || 0})
                </div>
                <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                  The same exercises as Today, including ones you already finished.
                </div>
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              fontSize: '0.9rem',
            }}
          >
            {overallScore.total > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                }}
              >
                <span>
                  <strong>Current Session:</strong> {overallScore.correct}/
                  {overallScore.total} (
                  {Math.round(
                    (overallScore.correct / overallScore.total) * 100
                  )}
                  %)
                </span>
                <span>
                  <strong>Mode:</strong>{' '}
                  {practiceMode.charAt(0).toUpperCase() + practiceMode.slice(1)}{' '}
                  • {filteredQuestions.length} exercises available
                </span>
              </div>
            )}
            {overallScore.total === 0 &&
              Object.keys(questionResults).length > 0 && (
                <span>
                  <strong>Mode:</strong>{' '}
                  {practiceMode.charAt(0).toUpperCase() + practiceMode.slice(1)}{' '}
                  • {filteredQuestions.length} exercises available
                </span>
              )}
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {Object.keys(questionResults).length > 0 && (
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      'This will clear all your Sentence Equivalence progress. Are you sure?'
                    )
                  ) {
                    clearAllProgress();
                  }
                }}
                style={{
                  background: 'var(--liwaza-error-container)',
                  color: 'var(--liwaza-on-error-container)',
                  border: '1px solid var(--liwaza-error)',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                }}
              >
                Reset Progress
              </button>
            )}
            <button
              onClick={handleStartPractice}
              disabled={filteredQuestions.length === 0}
              style={{
                background:
                  filteredQuestions.length > 0
                    ? 'var(--liwaza-primary)'
                    : 'var(--liwaza-surface-variant)',
                color:
                  filteredQuestions.length > 0
                    ? 'white'
                    : 'var(--liwaza-on-surface-variant)',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor:
                  filteredQuestions.length > 0 ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 600,
              }}
            >
              {filteredQuestions.length > 0 ? (
                <>
                  <FiTarget />
                  {practiceMode === 'unmastered'
                    ? availablePracticeModes.pending === 0
                      ? "Today's exercises are done"
                      : "Start today's exercises"
                    : practiceMode === 'attempted'
                      ? 'Retake missed exercises'
                      : practiceMode === 'mastered'
                        ? 'Review mastered'
                        : "Start today's exercises"}
                </>
              ) : (
                <>
                  <FiLock />
                  No Exercises Available
                </>
              )}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem',
          }}
        >
          {(() => {
            // Sort questions based on practice mode to show relevant ones first
            let sortedQuestions = [...practiceQuestions];

            if (practiceMode === 'attempted') {
              sortedQuestions.sort((a, b) => {
                if (a.status === 'attempted' && b.status !== 'attempted')
                  return -1;
                if (a.status !== 'attempted' && b.status === 'attempted')
                  return 1;
                return 0;
              });
            } else if (practiceMode === 'mastered') {
              sortedQuestions.sort((a, b) => {
                if (a.status === 'mastered' && b.status !== 'mastered')
                  return -1;
                if (a.status !== 'mastered' && b.status === 'mastered')
                  return 1;
                return 0;
              });
            }

            return sortedQuestions;
          })().map((question, index) => {
            const questionResult = questionResults[question.id];
            const isCompleted = questionResult?.isCompleted || false;
            const isMastered = questionResult?.mastered || false;

            // Check if this question is unlocked based on practice mode selection
            const isUnlocked = (() => {
              if (practiceMode === 'attempted') return question.role === 'review';
              if (practiceMode === 'mastered') return question.role === 'mastered';
              return isTodayQuestion(question);
            })();

            if (!isUnlocked) {
              return null;
            }

            // Check if this question is in the current practice mode filter (always true now since unlocking handles filtering)
            const isInCurrentMode = true;

            return (
              <div
                key={question.id}
                style={{
                  background:
                    isCompleted && isUnlocked
                      ? isMastered
                        ? 'var(--liwaza-success-container)'
                        : 'var(--liwaza-error-container)'
                      : question.status === 'mastered'
                        ? 'var(--liwaza-success-container)'
                        : question.status === 'attempted'
                          ? 'var(--liwaza-warning-container)'
                          : 'var(--liwaza-surface-container)',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  border:
                    isCompleted && isUnlocked
                      ? isMastered
                        ? '2px solid var(--liwaza-success)'
                        : '2px solid var(--liwaza-error)'
                      : question.status === 'mastered'
                        ? '2px solid var(--liwaza-success)'
                        : question.status === 'attempted'
                          ? '2px solid var(--liwaza-warning)'
                          : '1px solid var(--liwaza-outline-variant)',
                  cursor: isUnlocked && isInCurrentMode ? 'pointer' : 'default',
                  opacity: isUnlocked ? 1 : 0.6,
                  position: 'relative',
                }}
                onClick={() => {
                  if (isUnlocked && isInCurrentMode) {
                    const availableQuestions = getFilteredQuestions();
                    const questionIndex = availableQuestions.findIndex(
                      (q) => q.id === question.id
                    );
                    if (questionIndex !== -1) {
                      setSelectedQuestion(shuffleQuestionOptions(question));
                      setCurrentQuestionIndex(questionIndex);
                      setUserAnswers([]);
                      setShowResults(false);
                      setExerciseStartTime(Date.now());
                    }
                  }
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1rem',
                  }}
                >
                  <h3
                    style={{
                      color:
                        isCompleted && isUnlocked
                          ? isMastered
                            ? 'var(--liwaza-on-success-container)'
                            : 'var(--liwaza-on-error-container)'
                          : question.status === 'mastered'
                            ? 'var(--liwaza-on-success-container)'
                            : question.status === 'attempted'
                              ? 'var(--liwaza-on-warning-container)'
                              : 'var(--liwaza-on-surface)',
                      fontSize: '1.1rem',
                      margin: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Exercise{' '}
                    {practiceQuestions.findIndex((q) => q.id === question.id) +
                      1}
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}
                  >
                    {/* Session Status Badge - has priority */}
                    {isCompleted && isUnlocked && (
                      <span
                        style={{
                          background: isMastered
                            ? 'var(--liwaza-success)'
                            : 'var(--liwaza-error)',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isMastered ? <FiCheck size={12} /> : <FiX size={12} />}
                        {isMastered ? 'Session Complete' : 'Review Needed'}
                      </span>
                    )}

                    {/* Only show other badges if no session status badge */}
                    {!(isCompleted && isUnlocked) && (
                      <>
                        {/* Status Badge */}
                        <span
                          style={{
                            background:
                              question.status === 'mastered'
                                ? 'var(--liwaza-success)'
                                : question.status === 'attempted'
                                  ? 'var(--liwaza-warning)'
                                  : 'var(--liwaza-info)',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {question.status === 'mastered' ? (
                            <FiAward size={12} />
                          ) : question.status === 'attempted' ? (
                            <FiRefreshCw size={12} />
                          ) : (
                            <></>
                          )}
                          {question.carriedOver
                            ? 'From yesterday'
                            : question.role === 'today' && question.status === 'pending'
                              ? 'Today'
                              : question.role === 'today' && question.status !== 'pending'
                                ? 'Done today'
                                : question.status === 'mastered'
                                  ? 'Mastered'
                                  : question.status === 'attempted'
                                    ? 'Missed earlier'
                                    : 'Today'}
                        </span>
                      </>
                    )}

                    {!isUnlocked && (
                      <span
                        style={{
                          background: 'var(--liwaza-surface-variant)',
                          color: 'var(--liwaza-on-surface-variant)',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <FiLock size={12} />
                        Locked
                      </span>
                    )}
                  </div>
                </div>

                <p
                  style={{
                    color:
                      isCompleted && isUnlocked
                        ? isMastered
                          ? 'var(--liwaza-on-success-container)'
                          : 'var(--liwaza-on-error-container)'
                        : question.status === 'mastered'
                          ? 'var(--liwaza-on-success-container)'
                          : question.status === 'attempted'
                            ? 'var(--liwaza-on-warning-container)'
                            : 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                    marginBottom: '1rem',
                    lineHeight: 1.5,
                  }}
                >
                  {question.sentence?.length > 120
                    ? `${question.sentence.substring(0, 120)}...`
                    : question.sentence}
                </p>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--liwaza-on-surface-variant)',
                      fontSize: '0.8rem',
                    }}
                  >
                    {question.difficulty &&
                      `Difficulty: ${question.difficulty}`}
                    {question.sourceWord && ` • Word: ${question.sourceWord}`}
                  </span>

                  {isUnlocked && isInCurrentMode && (
                    <button
                      style={{
                        background:
                          question.status === 'mastered'
                            ? 'var(--liwaza-success)'
                            : question.status === 'attempted'
                              ? 'var(--liwaza-warning)'
                              : 'var(--liwaza-primary)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const availableQuestions = getFilteredQuestions();
                        const questionIndex = availableQuestions.findIndex(
                          (q) => q.id === question.id
                        );
                        if (questionIndex !== -1) {
                          setSelectedQuestion(shuffleQuestionOptions(question));
                          setCurrentQuestionIndex(questionIndex);
                          setUserAnswers([]);
                          setShowResults(false);
                          setExerciseStartTime(Date.now());
                        }
                      }}
                    >
                      {question.status === 'mastered'
                        ? 'Review'
                        : question.status === 'attempted'
                          ? 'Retake'
                          : 'Practice Now'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderQuestionPractice = () => {
    if (!selectedQuestion) return null;

    const isCorrect = checkAnswerCorrectness();
    const scorePercentage =
      overallScore.total > 0
        ? Math.round((overallScore.correct / overallScore.total) * 100)
        : 0;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: 'var(--liwaza-surface)',
            padding: '2rem',
            borderRadius: '12px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
            }}
          >
            <div>
              <h3 style={{ color: 'var(--liwaza-on-surface)', margin: 0 }}>
                Exercise {currentQuestionIndex + 1} of{' '}
                {getFilteredQuestions().length}
              </h3>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginTop: '0.5rem',
                }}
              >
                <span
                  style={{
                    color: 'var(--liwaza-on-surface-variant)',
                    fontSize: '0.9rem',
                  }}
                >
                  Score: {overallScore.correct}/{overallScore.total} (
                  {scorePercentage}%)
                </span>
                {selectedQuestion.difficulty && (
                  <span
                    style={{
                      background: 'var(--liwaza-secondary-container)',
                      color: 'var(--liwaza-on-secondary-container)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                    }}
                  >
                    {selectedQuestion.difficulty}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setSelectedQuestion(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--liwaza-on-surface-variant)',
                cursor: 'pointer',
                fontSize: '1.5rem',
              }}
            >
              ×
            </button>
          </div>

          {/* Question Text */}
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1.5rem',
              borderRadius: '8px',
              marginBottom: '2rem',
            }}
          >
            <p
              style={{
                color: 'var(--liwaza-on-surface)',
                fontSize: '1.1rem',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {selectedQuestion.sentence}
            </p>
          </div>

          <div
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            Select exactly 2 words that create equivalent meanings
          </div>

          {/* Answer Options */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            {(selectedQuestion.options || []).map((option, index) => {
              const isSelected = userAnswers.includes(option);
              const correctAnswers = selectedQuestion.correctAnswers || [];
              const isCorrect = showResults && correctAnswers.includes(option);
              const isIncorrectSelection =
                showResults && isSelected && !isCorrect;

              let buttonStyle = {
                padding: '0.75rem',
                borderRadius: '6px',
                cursor: showResults ? 'default' : 'pointer',
                textAlign: 'left',
                position: 'relative',
                transition: 'all 0.2s',
                fontSize: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              };

              if (showResults) {
                if (isCorrect) {
                  buttonStyle = {
                    ...buttonStyle,
                    background: 'var(--liwaza-success-container)',
                    color: 'var(--liwaza-on-success-container)',
                    border: '2px solid var(--liwaza-success)',
                  };
                } else if (isIncorrectSelection) {
                  buttonStyle = {
                    ...buttonStyle,
                    background: 'var(--liwaza-error-container)',
                    color: 'var(--liwaza-on-error-container)',
                    border: '2px solid var(--liwaza-error)',
                  };
                } else {
                  buttonStyle = {
                    ...buttonStyle,
                    background: 'var(--liwaza-surface-container-highest)',
                    color: 'var(--liwaza-on-surface-variant)',
                    border: '1px solid var(--liwaza-outline-variant)',
                    opacity: 0.7,
                  };
                }
              } else {
                if (isSelected) {
                  buttonStyle = {
                    ...buttonStyle,
                    background: 'var(--liwaza-primary-container)',
                    color: 'var(--liwaza-on-primary-container)',
                    border: '2px solid var(--liwaza-primary)',
                  };
                } else {
                  buttonStyle = {
                    ...buttonStyle,
                    background: 'var(--liwaza-surface-container-highest)',
                    color: 'var(--liwaza-on-surface)',
                    border: '1px solid var(--liwaza-outline-variant)',
                  };
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => !showResults && handleAnswerToggle(option)}
                  disabled={showResults}
                  style={buttonStyle}
                >
                  <span>{option}</span>
                  {showResults && isCorrect && (
                    <FiCheck style={{ color: 'var(--liwaza-success)' }} />
                  )}
                  {showResults && isIncorrectSelection && (
                    <FiX style={{ color: 'var(--liwaza-error)' }} />
                  )}
                  {isSelected && !showResults && (
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: 'var(--liwaza-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                      }}
                    >
                      {userAnswers.indexOf(option) + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Feedback and Controls */}
          {showResults ? (
            <>
              <div
                style={{
                  background: isCorrect
                    ? 'var(--liwaza-success-container)'
                    : 'var(--liwaza-error-container)',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                  }}
                >
                  {isCorrect ? (
                    <FiCheck
                      size={20}
                      style={{ color: 'var(--liwaza-on-success-container)' }}
                    />
                  ) : (
                    <FiX
                      size={20}
                      style={{ color: 'var(--liwaza-on-error-container)' }}
                    />
                  )}
                  <h4
                    style={{
                      color: isCorrect
                        ? 'var(--liwaza-on-success-container)'
                        : 'var(--liwaza-on-error-container)',
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isCorrect ? (
                      <>
                        <FiAward style={{ marginRight: '0.5rem' }} />
                        Excellent!
                      </>
                    ) : (
                      <>
                        <FiBookOpen style={{ marginRight: '0.5rem' }} />
                        Keep Learning!
                      </>
                    )}
                  </h4>
                </div>
                <p
                  style={{
                    color: isCorrect
                      ? 'var(--liwaza-on-success-container)'
                      : 'var(--liwaza-on-error-container)',
                    lineHeight: 1.6,
                    margin: '0 0 1rem 0',
                  }}
                >
                  <strong>Explanation:</strong>{' '}
                  {renderGreExplanationEmphasis(selectedQuestion.explanation)}
                </p>
                <p
                  style={{
                    color: isCorrect
                      ? 'var(--liwaza-on-success-container)'
                      : 'var(--liwaza-on-error-container)',
                    margin: 0,
                    fontSize: '0.9rem',
                    opacity: 0.9,
                  }}
                >
                  <strong>Correct answers:</strong>{' '}
                  {(selectedQuestion.correctAnswers || []).join(', ')}
                </p>
              </div>

              {/* Get Started With Component - After submitting answer */}
              <div style={{ margin: '1.5rem 0' }}>
                <GetStartedWith
                  title="Need help understanding this question?"
                  spaceName="Verbal vault"
                  humanCoachTitle="Ask a GRE coach"
                  liwazaAITitle="Ask AI"
                  communityTitle="Ask other students"
                  showHumanCoach={true}
                  showLiwazaAI={true}
                  showCommunity={true}
                  aifirstmessage={`I need help understanding this Sentence Equivalence question: "${selectedQuestion?.sentence}"`}
                  aicontext={`Sentence Equivalence question: "${selectedQuestion?.sentence}" and explanation: ${selectedQuestion?.explanation} and correct answer: ${selectedQuestion?.correct_answer} and options: ${selectedQuestion?.options}`}
                />
              </div>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                Selected: {userAnswers.length}/2
              </div>
              <button
                onClick={handleSubmitQuestion}
                disabled={userAnswers.length !== 2}
                style={{
                  background:
                    userAnswers.length === 2
                      ? 'var(--liwaza-primary)'
                      : 'var(--liwaza-surface-variant)',
                  color:
                    userAnswers.length === 2
                      ? 'white'
                      : 'var(--liwaza-on-surface-variant)',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: userAnswers.length === 2 ? 'pointer' : 'not-allowed',
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                Submit Answer
              </button>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                color: 'var(--liwaza-on-surface-variant)',
                fontSize: '0.9rem',
              }}
            >
              Progress: {currentQuestionIndex + 1}/
              {getFilteredQuestions().length}
            </div>

            {showResults && (
              <button
                onClick={handleNextQuestion}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                }}
              >
                {currentQuestionIndex + 1 < getFilteredQuestions().length ? (
                  <>
                    Next Exercise <FiArrowRight />
                  </>
                ) : (
                  <>
                    Complete Session <FiCheck />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSessionSummary = () => {
    if (selectedQuestion !== null || sessionProgress.length === 0) {
      return null;
    }

    const scorePercentage = Math.round(
      (overallScore.correct / overallScore.total) * 100
    );
    const masteredCount = sessionProgress.filter(
      (result) => result.mastered
    ).length;
    const totalCompleted = Object.keys(questionResults).length;
    return (
      <div
        style={{
          background: 'var(--liwaza-surface-container)',
          padding: '2rem',
          borderRadius: '12px',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h2
          style={{
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiAward />
          Session Summary
        </h2>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              background: 'var(--liwaza-primary-container)',
              padding: '1.5rem',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {totalCompleted}
            </div>
            <div
              style={{
                color: 'var(--liwaza-on-primary-container)',
                fontSize: '0.9rem',
              }}
            >
              Questions Completed
            </div>
          </div>

          <div
            style={{
              background: 'var(--liwaza-success-container)',
              padding: '1.5rem',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-success)',
                marginBottom: '0.5rem',
              }}
            >
              {scorePercentage}%
            </div>
            <div
              style={{
                color: 'var(--liwaza-on-success-container)',
                fontSize: '0.9rem',
              }}
            >
              Overall Accuracy
            </div>
          </div>

          <div
            style={{
              background: 'var(--liwaza-warning-container)',
              padding: '1.5rem',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: 'var(--liwaza-warning)',
                marginBottom: '0.5rem',
              }}
            >
              {masteredCount}
            </div>
            <div
              style={{
                color: 'var(--liwaza-on-warning-container)',
                fontSize: '0.9rem',
              }}
            >
              Questions Mastered
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: 'center',
          }}
        >
          <h3
            style={{
              color: 'var(--liwaza-on-surface)',
              marginBottom: '1rem',
            }}
          >
            {scorePercentage >= 80 ? (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiAward /> Excellent work!
              </span>
            ) : scorePercentage >= 60 ? (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiCheck /> Good progress!
              </span>
            ) : (
              <span
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <FiBookOpen /> Keep practicing!
              </span>
            )}
          </h3>
          <p
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              marginBottom: '1.5rem',
            }}
          >
            {scorePercentage >= 80
              ? "You're demonstrating strong sentence equivalence skills!"
              : scorePercentage >= 60
                ? "You're on the right track. Focus on understanding context clues."
                : 'Continue practicing to improve your accuracy and word pairing skills.'}
          </p>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={() =>
                navigate('/exams/gre/', {
                  state: { activeCategory: 'verbalprogress' },
                })
              }
              style={{
                background: 'var(--liwaza-primary)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <FiTrendingUp />
              View Progress
            </button>

            <button
              onClick={handleStartPractice}
              style={{
                background: 'var(--liwaza-surface-container-highest)',
                color: 'var(--liwaza-on-surface)',
                border: '1px solid var(--liwaza-outline)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <FiRefreshCw />
              Practice More
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render unlock progress section
  const renderUnlockProgress = () => {
    if (!vocabularyProgress || !unlockStatus) return null;

    const { currentMilestone, nextMilestone, progress } = unlockStatus;
    const masteredWords = vocabularyProgress.masteredWords || 0;

    return (
      <div
        style={{
          background:
            'linear-gradient(135deg, var(--liwaza-primary-container) 0%, var(--liwaza-secondary-container) 100%)',
          padding: '2rem',
          borderRadius: '16px',
          marginBottom: '2rem',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          }}
        />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
            }}
          >
            <div>
              <h2
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  margin: '0 0 0.5rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiAward />
                {currentMilestone.title}
              </h2>
              <p
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  opacity: 0.9,
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(currentMilestone.description),
                }}
              />
            </div>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                padding: '1rem',
                borderRadius: '12px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: 'var(--liwaza-on-primary-container)',
                }}
              >
                {masteredWords}
              </div>
              <div
                style={{
                  fontSize: '0.9rem',
                  color: 'var(--liwaza-on-primary-container)',
                  opacity: 0.8,
                }}
              >
                Words Mastered
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem',
              }}
            >
              <span
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  fontWeight: 600,
                }}
              >
                Today's set: {availablePracticeModes.pending ?? unlockStatus.unlockedExercises} left of {unlockStatus.exerciseStats?.total || unlockStatus.unlockedExercises}
              </span>
              {nextMilestone && (
                <span
                  style={{
                    color: 'var(--liwaza-on-primary-container)',
                    fontSize: '0.9rem',
                    opacity: 0.8,
                  }}
                >
                  Next unlock: {nextMilestone.words - masteredWords} more words
                </span>
              )}
            </div>

            {nextMilestone && (
              <div
                style={{
                  height: '8px',
                  background: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(progress * 100, 100)}%`,
                    height: '100%',
                    background: 'var(--liwaza-success)',
                    borderRadius: '4px',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            )}
          </div>

          {masteredWords === 0 ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  margin: '0 0 1rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiStar /> Start your powerful GRE vocabulary journey! Master
                words through engaging article reading to unlock exciting
                Sentence Equivalence exercises.
              </p>
              <button
                onClick={() =>
                  navigate('/exams/gre/', {
                    state: { activeCategory: 'vocabularymastery' },
                  })
                }
                style={{
                  background:
                    'linear-gradient(135deg, var(--liwaza-primary) 0%, var(--liwaza-secondary) 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: '0 auto',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  transform: 'scale(1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05) translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1) translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
              >
                <FiZap style={{ fontSize: '1.2rem' }} />
                Unlock More By Mastering Words!
              </button>
            </div>
          ) : nextMilestone &&
            unlockStatus.unlockedExercises < unlockStatus.totalExercises ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  margin: '0 0 1rem 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiTarget /> Master {nextMilestone.words - masteredWords} more
                words to unlock the next set of exercises!
              </p>
              <button
                onClick={() =>
                  navigate('/exams/gre/', {
                    state: { activeCategory: 'vocabularymastery' },
                  })
                }
                style={{
                  background:
                    'linear-gradient(135deg, var(--liwaza-primary) 0%, var(--liwaza-secondary) 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '1rem 2rem',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: '0 auto',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  transform: 'scale(1)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05) translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1) translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
                }}
              >
                <FiZap style={{ fontSize: '1.2rem' }} />
                Unlock More Power!
              </button>
            </div>
          ) : !nextMilestone && masteredWords > 0 ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}
            >
              <p
                style={{
                  color: 'var(--liwaza-on-primary-container)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                }}
              >
                <FiAward /> All exercises unlocked! You have reached the highest
                vocabulary milestone.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-on-surface)',
        }}
      >
        <div className="layout-loading">
          <div className="loading-spinner"></div>
          <p>Loading sentence equivalence exercises...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-error)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <h2>Error Loading Exercises</h2>
        <p>{getErrorMessage(error, 'Failed to load exercises.')}</p>
        {authMismatch && (
          <>
            <p>
              Signed in as{' '}
              <strong>{authMismatch.currentEmail || 'Unknown user'}</strong>
              {authMismatch.requestedEmail
                ? `, but this content was requested for ${authMismatch.requestedEmail}.`
                : '.'}
            </p>
            <button
              onClick={() => logout()}
              style={{
                background: 'var(--liwaza-primary)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '0.5rem',
              }}
            >
              Log out and switch account
            </button>
          </>
        )}
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1
          style={{
            fontSize: '2rem',
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1rem',
          }}
        >
          Sentence Equivalence
        </h1>
        <p
          style={{
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '1.1rem',
            marginBottom: '2rem',
          }}
        >
          Master GRE Sentence Equivalence with gamified practice. Unlock
          exercises by mastering vocabulary words through article reading and
          learn to identify words with equivalent meanings.
        </p>
      </div>

      {renderUnlockProgress()}
      {renderTips()}

      {/* Get Started With Component - Before starting exercises */}
      {!selectedQuestion && sessionProgress.length === 0 && (
        <div style={{ margin: '2rem 0' }}>
          <GetStartedWith
            title="Need help with Sentence Equivalence strategies?"
            spaceName="Verbal vault"
            humanCoachTitle="Ask a GRE coach"
            liwazaAITitle="Ask AI"
            communityTitle="Ask other students"
            showHumanCoach={true}
            showLiwazaAI={true}
            showCommunity={true}
          />
        </div>
      )}

      {renderPracticeSection()}
      {selectedQuestion && renderQuestionPractice()}
      {renderSessionSummary()}
    </div>
  );
}
